<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-red-50/50 dark:bg-red-900/10">
        <div>
          <h2 class="text-lg font-bold text-red-700 dark:text-red-400 flex items-center gap-2">
            <RotateCcw class="w-5 h-5" />
            {{ form.id ? 'Autorizar Reversión de Pago' : 'Solicitar Reversión de Pago (Recibo)' }}
          </h2>
          <p class="text-xs text-red-600/70">Esta acción anulará el recibo de caja y devolverá el saldo al crédito.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-red-100 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <div class="flex-1 overflow-y-auto p-6 custom-scrollbar">
        <form @submit.prevent="handleSubmit" id="reversionPaymentForm" class="space-y-6">

          <div v-if="!form.id" class="bg-slate-50 dark:bg-slate-800/30 p-5 rounded-xl border border-slate-200 dark:border-slate-700">
            <label class="text-xs font-semibold text-slate-500 uppercase mb-2 block">Buscar Recibo de Caja (Últimos 30 días)</label>
            <div class="relative">
              <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input v-model="paymentSearchQuery" @focus="showPaymentDropdown = true" type="text" placeholder="Escribe el ID del recibo o nombre del cliente..." :class="[inputClasses, 'pl-10']" />
            </div>

            <div v-if="showPaymentDropdown && filteredPayments.length > 0" class="absolute z-10 w-full max-w-2xl mt-1 bg-white dark:bg-slate-800 border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
              <div v-for="p in filteredPayments" :key="p.id" @click="selectPayment(p)" class="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100">
                <div class="flex justify-between items-center">
                  <div class="font-bold text-sm">{{ p.customer_name }}</div>
                  <div class="font-mono text-emerald-600 font-bold">+${{ p.amount_paid }}</div>
                </div>
                <div class="text-[10px] text-slate-500 flex justify-between mt-1">
                  <span>Recibo: {{ p.payment_code }}</span>
                  <span>Fecha: {{ p.payment_date }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="selectedPayment" class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 class="text-xs font-bold text-slate-400 uppercase mb-4 border-b pb-2">Detalles del Recibo</h3>
              <div class="space-y-3 text-sm">
                <div class="flex justify-between"><span class="text-slate-500">Cliente:</span> <span class="font-bold">{{ selectedPayment.customer_name }}</span></div>
                <div class="flex justify-between"><span class="text-slate-500">ID Recibo:</span> <span class="font-mono">{{ selectedPayment.id || selectedPayment.payment_id }}</span></div>
                <div class="flex justify-between"><span class="text-slate-500">Fecha Pago:</span> <span>{{ selectedPayment.payment_date || selectedPayment.created_at }}</span></div>
                <div class="flex justify-between items-center bg-red-50 p-2 rounded-lg mt-2">
                  <span class="text-red-600 font-bold">Monto a Extraer de Caja:</span>
                  <span class="font-mono font-black text-red-700 text-lg">-${{ Number(selectedPayment.amount_paid).toFixed(2) }}</span>
                </div>
              </div>
            </div>

            <div class="space-y-4">
              <div>
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Motivo de la Reversión <span class="text-red-500">*</span></label>
                <textarea v-model="form.motive" required rows="4" placeholder="Explique por qué se anula este recibo de pago..." :disabled="form.id && !canAuthorize" :class="inputClasses"></textarea>
              </div>

              <div v-if="canAuthorize" class="bg-amber-50 border border-amber-200 p-4 rounded-xl flex items-start gap-3">
                <input type="checkbox" v-model="form.is_authorized" id="authCheckPayment" class="mt-1 w-4 h-4 text-amber-600 rounded">
                <div>
                  <label for="authCheckPayment" class="font-bold text-amber-800 text-sm cursor-pointer">Autorizar y Ejecutar Reversión Inmediata</label>
                  <p class="text-xs text-amber-700 mt-1">Al marcar esto, se restará el dinero de la caja y el capital del crédito volverá a subir. Las cuotas pagadas se reactivarán.</p>
                </div>
              </div>
            </div>
          </div>

        </form>
      </div>

      <div class="px-6 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 flex justify-end gap-3">
        <button @click="$emit('close')" type="button" class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50">Cancelar</button>

        <button type="submit" form="reversionPaymentForm" :disabled="isSaving || !selectedPayment" class="px-6 py-2 text-sm font-bold text-white rounded-xl shadow-lg transition-all flex items-center gap-2 disabled:opacity-50" :class="form.is_authorized ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'">
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          <RotateCcw v-else class="w-4 h-4" />
          {{ form.is_authorized ? 'Ejecutar Reversión' : 'Solicitar Autorización' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed, onMounted } from 'vue';
import { X, Search, RotateCcw, Loader2 } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';
import Swal from 'sweetalert2';

const props = defineProps({ show: Boolean, reversionToEdit: Object });
const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();

const inputClasses = "w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20";

const isSaving = ref(false);
const recentPayments = ref([]);
const paymentSearchQuery = ref('');
const showPaymentDropdown = ref(false);
const selectedPayment = ref(null);

const canAuthorize = ref(false);

const form = reactive({
  id: null,
  payment_id: '',
  loan_id: '',
  motive: '',
  is_authorized: false
});

onMounted(async () => {
  const user = SecureStorage.getItem('llevo_user');
  if (!user) return;

  canAuthorize.value = user.role_id === 1 || user.role === 'ADMIN';

  if (!props.reversionToEdit?.id) {
    try {
      // IDEALMENTE: Traer los últimos pagos realizados para poder seleccionarlos
      const res = await apiClient.get(`/payments?company_id=${user.company} `);
      recentPayments.value = res.data.data || res.data;
    } catch (e) { console.error("Error cargando pagos"); }
  }
});

const filteredPayments = computed(() => {
  if (!paymentSearchQuery.value) return recentPayments.value.slice(0, 5);
  const q = paymentSearchQuery.value.toLowerCase();
  return recentPayments.value.filter(p => p.customer_name?.toLowerCase().includes(q) || p.id?.toLowerCase().includes(q));
});

const selectPayment = (payment) => {
  selectedPayment.value = payment;
  form.payment_id = payment.id;
  form.loan_id = payment.loan_id;
  paymentSearchQuery.value = '';
  showPaymentDropdown.value = false;
};

watch(() => props.show, (val) => {
  if (val) {
    if (props.reversionToEdit && Object.keys(props.reversionToEdit).length > 0) {
      Object.assign(form, props.reversionToEdit);
      selectedPayment.value = {
        customer_name: form.customer_name,
        payment_id: form.payment_id,
        amount_paid: form.amount_paid,
        payment_date: form.created_at
      };
      form.is_authorized = false;
    } else {
      form.id = null; form.payment_id = ''; form.loan_id = ''; form.motive = ''; form.is_authorized = false;
      selectedPayment.value = null;
    }
  }
});

const handleSubmit = async () => {
  if (!form.payment_id) return showError('Debe seleccionar un recibo válido.');

  const result = await Swal.fire({
    title: form.is_authorized ? '¿Revertir Recibo?' : '¿Solicitar Reversión?',
    text: form.is_authorized ? "Esta acción creará un saldo negativo en caja y aumentará la deuda del cliente." : "La solicitud pasará a estado Pendiente.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: form.is_authorized ? '#dc2626' : '#f59e0b',
    cancelButtonText: 'Cancelar',
    confirmButtonText: form.is_authorized ? 'Sí, Revertir Ahora' : 'Enviar Solicitud'
  });

  if (!result.isConfirmed) return;

  isSaving.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    const payload = {
      ...form,
      company_id: user.company,
      requested_by: user.id,
      action: form.is_authorized ? 'EXECUTE' : 'REQUEST'
    };

    if (form.id) {
      await apiClient.put(`/payment_reversions/${form.id}`, payload);
      showSuccess(form.is_authorized ? 'Recibo Revertido Exitosamente' : 'Solicitud Actualizada');
    } else {
      await apiClient.post('/payment_reversions', payload);
      showSuccess(form.is_authorized ? 'Recibo Revertido Exitosamente' : 'Solicitud Enviada a Aprobación');
    }
    emit('saved');
    emit('close');
  } catch (e) {
    showError(e.response?.data?.error || 'Error al procesar la reversión.');
  } finally {
    isSaving.value = false;
  }
};
</script>